/**
 * Trims leading/trailing whitespace from all string values in a search filter object,
 * so padded input (e.g. " 12345") matches the same results as "12345".
 * Non-string values (numbers, dates, booleans) are passed through unchanged.
 */
export function trimFilters<T extends Record<string, unknown>>(filters: T): T {
    return Object.fromEntries(
        Object.entries(filters).map(([key, value]) =>
            [key, typeof value === "string" ? value.trim() : value])
    ) as T;
}

/**
 * Seeds a search form's default filter shape with values restored from the URL.
 * Only keys the form already declares are applied, so an unexpected query param
 * can't introduce fields the form doesn't own.
 */
export function applyInitialFilters<T extends Record<string, unknown>>(
    defaults: T,
    initialFilters?: Record<string, unknown>
): T {
    const initial = { ...defaults };
    if (!initialFilters) return initial;

    for (const [key, value] of Object.entries(initialFilters)) {
        if (value && key in initial) {
            (initial as Record<string, unknown>)[key] = value;
        }
    }

    return initial;
}

/**
 * Builds an all-empty map of a form's filter keys. Handed to syncFiltersToUrl on
 * reset so every param the form owns is removed, including fields the form itself
 * resets to a non-empty default (a first-option dropdown value, say).
 */
export function clearedFilterKeys(defaults: Record<string, unknown>): Record<string, string> {
    return Object.fromEntries(Object.keys(defaults).map(key => [key, ""]));
}
